import { Alert, Stack, Box } from '@mui/material';
import { MuiFileInput } from 'mui-file-input';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import { Editor as TinyMCEEditor } from 'tinymce';

import { Application } from '@features/applications';
import MessagesList from '@features/messaging/components/MessagesList/MessagesList';
import SendMessageButtons from '@features/messaging/components/SendMessageButtons/SendMessageButtons';
import { useGetThread } from '@features/messaging/hooks/useGetThread';
import SavedMessagesSelect from '@features/savedMessages/components/SavedMessagesSelect/SavedMessagesSelect';
import { authContext } from '@staffedupusa/shared-components';
import RockerboxMessagesList from '@features/rockerboxDashboard/components/RockerboxMessagesList';
import BundledEditor from '@utils/BundledEditor';
import AIPromptModal from '../AIPromptModal/AIPromptModal';

type MessagingFormProps = {
  application?: Application;
  threadId?: number;
  fullHeight?: boolean;
  showMarkReadButton?: boolean;
  disabledUnreadButton?: boolean;
  onUnreadSuccess: () => void;
  isApplicant?: boolean | null;
  handlePageNo?: (newPageNo: number) => void;
};

const MessagingForm: FC<MessagingFormProps> = ({
  application,
  threadId,
  fullHeight,
  isApplicant,
  disabledUnreadButton,
  showMarkReadButton,
  onUnreadSuccess,
  handlePageNo,
}) => {
  const [message, setMessage] = useState('');
  const [errorText, setErrorText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [threadPage, setThreadPage] = useState(1);
  const [showAiPromptModal, setShowAiPromptModal] = useState(false);
  const editorRef = useRef<TinyMCEEditor | null>(null);
  const { thread, messages, isLoadingThread } = useGetThread({
    applicantId: application?.applicant?.id,
    ...(isApplicant
      ? { company_id: application?.job_listing?.company?.id }
      : {}),
    threadId,
    setErrorText,
    threadPage,
  });

  const {
    authData: { user },
  } = useContext(authContext);
  const loggedInUserId = user?.id;

  const onSendSuccess = () => {
    handlePageNo && handlePageNo(1);
    setMessage('');
    setFile(null);
    setThreadPage(1);
  };

  const handleInsertAIContent = (content: string) => {
    setMessage(content);
    setShowAiPromptModal(false);
  };

  useEffect(() => {
    setThreadPage(1);
  }, [application, threadId]);

  useEffect(() => {
    setMessage('');
    setErrorText('');
    setFile(null);
    setThreadPage(1);
  }, [threadId]);

  return (
    <>
      <Stack
        spacing={2}
        sx={{
          height: {
            xs: fullHeight ? 'calc(100vh - 88px)' : 'auto',
            sm: fullHeight ? '100vh' : 'auto',
          },
          justifyContent: 'flex-end',
        }}
      >
        {errorText && <Alert severity="error">{errorText}</Alert>}
        {messages && Boolean(messages.length) && (
          <>
            {user?.is_rockerbox ? (
              <RockerboxMessagesList
                messages={messages}
                fullHeight={fullHeight}
                headerData={thread}
                onEndReached={() => setThreadPage((prev) => prev + 1)}
                isLoading={isLoadingThread}
                loggedInUserId={loggedInUserId}
              />
            ) : (
              <MessagesList
                messages={messages}
                fullHeight={fullHeight}
                headerData={thread}
                onEndReached={() => setThreadPage((prev) => prev + 1)}
                isLoading={isLoadingThread}
                loggedInUserId={loggedInUserId}
                isApplicant={isApplicant}
              />
            )}
          </>
        )}
        <Stack
          spacing={3}
          sx={{
            pt: 0,
            pb: 3,
            px: 3,
            borderRadius: 0,
            boxShadow: 'none',
            borderTop: '1px solid rgba(128, 129, 140, .25)',
          }}
          style={{ marginTop: '0' }}
        >
          {!isApplicant && (
            <Box sx={{ pt: 3 }}>
              <SavedMessagesSelect setMessage={setMessage} message={message} />
            </Box>
          )}

          <BundledEditor
            onInit={(evt: any, editor: TinyMCEEditor | null) =>
              (editorRef.current = editor)
            }
            value={message}
            onEditorChange={(newValue: string) => setMessage(newValue)}
            init={{
              height: 180,
              menubar: false,
              plugins: ['lists', 'code', 'autolink', 'link', 'pagebreak'],
              toolbar:
                'spellchecker language spellcheckdialog | styles | bold italic | bullist numlist | link',
              spellchecker_dialog: true,
              spellchecker_rpc_url: 'localhost/ephox-spelling',
              spellchecker_language: 'en_US',
              spellchecker_active: true,
              browser_spellcheck: true,
              contextmenu: false,
            }}
          />
          <MuiFileInput
            variant="outlined"
            size="small"
            label="Attach files (pdf, doc, docx, jpg, jpeg, png)"
            value={file!}
            onChange={(newFile) => setFile(newFile)}
            InputProps={{
              placeholder: 'true',
              inputProps: {
                accept: '.pdf, .doc, .docx, .jpg, .jpeg, .png',
                placeholder: 'false',
              },
            }}
          />
          <SendMessageButtons
            message={message}
            threadId={threadId ?? thread?.id}
            setErrorText={setErrorText}
            disabledUnreadButton={disabledUnreadButton}
            showMarkReadButton={showMarkReadButton}
            onSendSuccess={onSendSuccess}
            file={file}
            application={application}
            recipientId={thread?.participant1?.id}
            company_id={thread?.company.id}
            onUnreadSuccess={onUnreadSuccess}
            isApplicant={isApplicant}
            setShowAiPromptModal={setShowAiPromptModal}
          />
        </Stack>
      </Stack>
      {showAiPromptModal && (
        <AIPromptModal
          isOpen={showAiPromptModal}
          handleInsertAIContent={handleInsertAIContent}
          handleClose={() => setShowAiPromptModal(false)}
        />
      )}
    </>
  );
};

export default MessagingForm;
